import type { JSX } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { SearchPageSortOption } from 'src/hooks/useSearchPage';

import SearchFilterAccordions from '../SearchFilterAccordions';

export type SortByControlProps<T extends string> = {
  onChange: (sort: T) => void;
  value: T;
  sortOptions: SearchPageSortOption<T>[];
};

const SortByControl = <T extends string>(props: SortByControlProps<T>): JSX.Element => {
  const { onChange, value, sortOptions } = props;
  const { sortByLabel } = useDictionary();

  if (sortOptions.length === 0) {
    return <></>;
  }

  const label = sortOptions.find((option) => option.value === value)?.label || '';

  return (
    <SearchFilterAccordions
      filterConfigs={[
        {
          name: 'sort',
          label,
          options: sortOptions,
        },
      ]}
      // We're reusing the SearchFilterAccordions component to render the sort options,
      // so we need to adapt the filterState to match the expected shape
      filterState={{
        sort: {
          setSelectedValues: (values) => {
            // We are trying to simulate radio button behavior with the
            // checkbox group, so we take the last selected value. If the
            // group reports an empty array (for example, after unchecking),
            // fall back to the default sort option.
            const nextValue = values[values.length - 1] ?? sortOptions[0].value;
            onChange(nextValue as T);
          },
          selectedValues: [value],
          label,
          clear() {},
          isSelected: () => true,
          toggleValue() {},
          options: sortOptions,
        },
      }}
      sectionLabel={sortByLabel}
    />
  );
};

export default SortByControl;
